import React from 'react'
import edgeBoostLogo from '../assets/images/EDGE_Boost_Logo.png'

/**
 * Renders the preloader with EDGE BOOST logo and loading bar
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Main loading title (default: "Loading...")
 * @param {string} props.subtitle - Loading subtitle/description (default: "")
 * @param {boolean} props.showLogo - Whether to show the logo (default: true)
 * @param {boolean} props.showText - Whether to show the loading text (default: true)
 * @param {string} props.variant - Loading variant: 'fullscreen', 'inline', 'minimal' (default: 'fullscreen')
 * 
 * @example
 * // Fullscreen loader (app initialization)
 * <Loader 
 *   title="Loading Your Edge" 
 *   subtitle="Setting up your betting bank account..."
 *   variant="fullscreen"
 * />
 * 
 * // Inline loader (content areas)
 * <Loader 
 *   title="Processing Deposit" 
 *   subtitle="Securing your funds..."
 *   variant="inline"
 * />
 * 
 * // Minimal loader (small components)
 * <Loader 
 *   variant="minimal" 
 *   showText={false}
 * />
 */
const Loader = ({ 
  title = "Loading...", 
  subtitle = "",
  showLogo = true,
  showText = true,
  variant = 'fullscreen'
}) => {
  // Fullscreen loader (covers entire viewport)
  if (variant === 'fullscreen') {
    return (
      <div className='preloader' style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        {showLogo && (
          <div className='logo-container' style={{
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            <img 
              src={edgeBoostLogo} 
              alt="EDGE BOOST" 
              style={{
                maxWidth: '300px',
                height: 'auto'
              }}
            />
          </div>
        )}
        
        {showText && (
          <div className='loading-text' style={{
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: '600',
              margin: '0 0 8px 0',
              fontFamily: 'Arial, sans-serif'
            }}>
              {title}
            </h3>
            {subtitle && (
              <p style={{
                color: '#cccccc',
                fontSize: '16px',
                margin: '0',
                fontFamily: 'Arial, sans-serif'
              }}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className='loading-bar-container' style={{
          width: '300px',
          height: '4px',
          backgroundColor: '#333333',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div className='loading-bar' style={{
            height: '100%',
            backgroundColor: '#007bff',
            borderRadius: '2px'
          }} />
        </div>
      </div>
    )
  }

  // Inline loader (for content areas)
  if (variant === 'inline') {
    return (
      <div className='inline-loader' style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px',
        minHeight: '200px'
      }}>
        {showLogo && (
          <div className='logo-container' style={{
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <img 
              src={edgeBoostLogo} 
              alt="EDGE BOOST" 
              style={{
                maxWidth: '150px',
                height: 'auto'
              }}
            />
          </div>
        )}
        
        {showText && (
          <div className='loading-text' style={{
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <h4 style={{
              color: '#FFFFFF',
              fontSize: '18px',
              fontWeight: '600',
              margin: '0 0 4px 0',
              fontFamily: 'Arial, sans-serif'
            }}>
              {title}
            </h4>
            {subtitle && (
              <p style={{
                color: '#999999',
                fontSize: '14px',
                margin: '0',
                fontFamily: 'Arial, sans-serif'
              }}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className='loading-bar-container' style={{
          width: '200px',
          height: '3px',
          backgroundColor: '#e9ecef',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div className='loading-bar' style={{
            height: '100%',
            backgroundColor: '#007bff',
            borderRadius: '2px'
          }} />
        </div>
      </div>
    )
  }

  // Minimal loader (for small components)
  if (variant === 'minimal') {
    return (
      <div className='minimal-loader' style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}>
        {showLogo && (
          <div className='logo-container' style={{
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            <img 
              src={edgeBoostLogo} 
              alt="EDGE BOOST" 
              style={{
                maxWidth: '80px',
                height: 'auto'
              }}
            />
          </div>
        )}
        
        <div className='loading-bar-container' style={{
          width: '120px',
          height: '2px',
          backgroundColor: '#e9ecef',
          borderRadius: '1px',
          overflow: 'hidden'
        }}>
          <div className='loading-bar' style={{
            height: '100%',
            backgroundColor: '#007bff',
            borderRadius: '1px'
          }} />
        </div>
      </div>
    )
  }

  // Default to fullscreen
  return null
}

/**
 * Convenience components for different use cases
 */

/**
 * Fullscreen loader for app initialization and major page transitions
 * @param {Object} props - Same props as Loader component
 */
export const FullscreenLoader = (props) => <Loader variant="fullscreen" {...props} />

/**
 * Inline loader for content areas and component loading
 * @param {Object} props - Same props as Loader component
 */
export const InlineLoader = (props) => <Loader variant="inline" {...props} />

/**
 * Minimal loader for small components and subtle loading states
 * @param {Object} props - Same props as Loader component
 */
export const MinimalLoader = (props) => <Loader variant="minimal" {...props} />

/**
 * Legacy fallback components for existing layouts
 * These maintain backward compatibility with existing Suspense fallbacks
 */
export const emptyLoading = () => <div />
export const loading = () => <InlineLoader title="Loading..." showLogo={false} />

export default Loader
